import { useEffect, useMemo, useState } from "react";
import { GeoJSON, MapContainer, TileLayer } from "react-leaflet";
import { ArrowLeft, Database, RefreshCw, Upload } from "lucide-react";
import { toast } from "sonner";
import { useOp } from "../../lib/useOp";
import { http, errMsg, downloadBlob } from "../../lib/api";
import { can, useAuth } from "../../lib/auth";
import { Btn, Card } from "../kit/Kit";
import { ATTR, BandChip, FitTo, Legend, TILE, colorIdx, fmt, tipHtml } from "./geo";
import { fDia } from "../../lib/trading";

const EST = { ok: "bg-emerald-50 text-emerald-700", error: "bg-rose-50 text-rose-700", pendiente: "bg-amber-50 text-amber-800" };

export const Fuentes = ({ fuentes, onReload }) => {
  const o = useOp("fuentes-progress");
  const { user } = useAuth();
  const imor = async (f) => {
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try { const r = await http.post("/riesgo/regional/carga/imor", fd); toast.success(`IMOR cargado (${r.data.filas} entidades)`); onReload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card title="Fuentes y fecha de corte" subtitle="Cada dato se guarda con fuente, URL y corte; si una descarga falla se conserva el último dato" testid="regional-fuentes"
      right={can(user, "risk.configurar") && <div className="flex flex-wrap gap-2">
        <Btn kind="secondary" disabled={o.busy} onClick={() => o.run("Actualización de fuentes públicas (SESNSP / INEGI)", (cfg) => http.post("/riesgo/regional/actualizar", {}, cfg), { background: true }).then(onReload).catch(() => null)} data-testid="regional-actualizar"><RefreshCw size={14} />Actualizar ahora</Btn>
        <Btn kind="ghost" onClick={() => downloadBlob("/riesgo/plantillas/imor", "plantilla_imor.csv")} data-testid="imor-plantilla"><Database size={14} />Plantilla IMOR</Btn>
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl bg-[#111] px-4 py-2.5 text-sm font-semibold text-white" data-testid="imor-upload"><Upload size={14} />Cargar IMOR CNBV<input type="file" accept=".csv" className="hidden" onChange={(e) => imor(e.target.files[0])} data-testid="imor-input" /></label>
      </div>}>
      <ul className="grid gap-2 md:grid-cols-2">{fuentes.map((f) => (
        <li key={f.id} className="min-w-0 rounded-xl border border-[#E2DDD5] px-4 py-3 text-sm" data-testid={`fuente-${f.id}`}>
          <div className="flex min-w-0 items-start justify-between gap-2"><p className="min-w-0 break-words font-semibold">{f.nombre}</p><span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold ${EST[f.estado] || EST.pendiente}`}>{f.estado === "ok" ? "Al día" : f.estado === "error" ? "Último dato" : "Pendiente"}</span></div>
          <p className="mt-1 break-all text-xs text-[#5C5850]">{f.corte ? `Corte ${fDia(f.corte, true)}` : ""}{f.archivo ? ` · ${f.archivo}` : ""}{f.actualizado ? ` · actualizado ${new Date(f.actualizado).toLocaleDateString("es-MX")}` : ""}</p>
          {f.error && <p className="mt-1 text-xs text-amber-800">{f.error}</p>}
          {f.url && <a href={f.url} target="_blank" rel="noreferrer" className="mt-1 block break-all text-xs text-[#E8643C] hover:underline" data-testid={`fuente-url-${f.id}`}><span className="sm:hidden">Ver fuente ↗</span><span className="hidden sm:inline">{f.url}</span></a>}
        </li>))}</ul>
      {o.bar && <div className="mt-4">{o.bar}</div>}
    </Card>
  );
};

export const MapaRiesgo = () => {
  const [geo, setGeo] = useState(null);
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);
  const [mun, setMun] = useState(null);
  const [ind, setInd] = useState("indice");
  const load = () => http.get("/riesgo/regional").then((r) => setData(r.data));
  useEffect(() => { http.get("/riesgo/geo/estados").then((r) => setGeo(r.data)); load(); }, []);
  useEffect(() => {
    if (!sel) return setMun(null);
    Promise.all([http.get(`/riesgo/geo/municipios/${sel}`), http.get(`/riesgo/regional?nivel=municipio&cve_ent=${sel}`)]).then(([g, r]) => setMun({ geo: g.data, rows: r.data.rows }));
  }, [sel]);
  const byCve = useMemo(() => Object.fromEntries((data?.rows || []).map((r) => [r.cve, r])), [data]);
  const munBy = useMemo(() => Object.fromEntries((mun?.rows || []).map((r) => [r.cve, r])), [mun]);
  if (!geo || !data) return <Card><p className="text-sm text-[#8C867A]">Cargando mapa nacional…</p></Card>;
  const labels = data.indicadores;
  const val = (r) => (!r ? null : ind === "indice" ? r.indice : r.ranks?.[ind]);
  const layer = (rows) => ({
    style: (f) => ({ fillColor: colorIdx(val(rows[f.properties.cvegeo])), weight: 0.8, color: "#fff", fillOpacity: 0.88 }),
    onEachFeature: (f, l) => {
      l.bindTooltip(tipHtml(rows[f.properties.cvegeo], labels), { sticky: true, className: "risk-tip" });
      l.on({ mouseover: () => l.setStyle({ weight: 2.4, color: "#111" }), mouseout: () => l.setStyle({ weight: 0.8, color: "#fff" }), click: () => !sel && setSel(f.properties.cvegeo) });
    },
  });
  const rank = [...(sel ? mun?.rows || [] : data.rows)].filter((r) => r.indice != null).sort((a, b) => b.indice - a.indice).slice(0, 10);
  return (
    <div className="space-y-6">
      <div className="grid gap-6 lg:grid-cols-12">
        <Card className="lg:col-span-8" testid="mapa-nacional" title={sel ? `${byCve[sel]?.nombre} · municipios` : "Mapa de calor nacional"}
          subtitle="Índice regional 0–100 por percentiles. Pasa el cursor para ver indicadores, fuente y corte; haz clic en un estado para ver sus municipios."
          right={<div className="flex flex-wrap items-center gap-2">
            {sel && <Btn kind="secondary" onClick={() => setSel(null)} data-testid="mapa-volver"><ArrowLeft size={14} />Nacional</Btn>}
            <select value={ind} onChange={(e) => setInd(e.target.value)} className="rounded-xl border border-[#E2DDD5] bg-white px-3 py-2 text-sm" data-testid="mapa-indicador">
              <option value="indice">Índice regional</option>{Object.entries(labels).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>}>
          <div className="h-[460px] overflow-hidden rounded-2xl border border-[#E2DDD5]">
            <MapContainer center={[23.6, -102.5]} zoom={5} scrollWheelZoom={false} style={{ height: "100%", background: "#F7F5F1" }}>
              <TileLayer url={TILE} attribution={ATTR} />
              {!sel && <GeoJSON key={`e-${ind}`} data={geo} {...layer(byCve)} />}
              {sel && mun && <><GeoJSON key={`m-${sel}-${ind}`} data={mun.geo} {...layer(munBy)} /><FitTo data={mun.geo} /></>}
              {!sel && <FitTo data={geo} />}
            </MapContainer>
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2"><Legend /><p className="text-[11px] text-[#8C867A]">Teselas © OpenStreetMap · Geometría INEGI Marco Geoestadístico</p></div>
        </Card>
        <Card className="lg:col-span-4" title={sel ? "Municipios con mayor índice" : "Estados con mayor índice"} testid="mapa-ranking">
          <ol className="space-y-2">{rank.map((r, i) => (
            <li key={r.cve} className="flex items-center gap-3 rounded-xl bg-[#FAF8F5] px-3 py-2 text-sm" data-testid={`ranking-${r.cve}`}>
              <span className="w-5 text-xs font-bold text-[#8C867A]">{i + 1}</span><span className="min-w-0 flex-1 truncate font-medium">{r.nombre}</span>
              <span className="tabular-nums font-bold">{fmt(r.indice, 1)}</span><BandChip b={r.banda} /></li>))}</ol>
        </Card>
      </div>
      <Fuentes fuentes={data.fuentes} onReload={load} />
    </div>
  );
};
